import * as React from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { listUserDealsAdmin } from "@/lib/admin-deals.functions";
import { AdminCreateDealPanel } from "@/components/admin/admin-create-deal-panel";
import {
  AdminProspectClaimPanel,
  ProspectBadge,
  useProspectStatus,
} from "@/components/admin/admin-prospect-claim-panel";

export const Route = createFileRoute("/_authed/admin/users_/$userId")({
  head: () => ({
    meta: [
      { title: "Admin user deals: CapitalDart" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AdminUserDealsPage,
});

const fmtDate = (v: string | null) =>
  v ? new Date(v).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }) : "";

const fmtMoney = (v: number | null) =>
  v === null ? "" : `$${Number(v).toLocaleString()}`;

function AdminUserDealsPage() {
  const { userId } = Route.useParams();
  const fetchDeals = useServerFn(listUserDealsAdmin);
  const q = useQuery({
    queryKey: ["admin-user-deals", userId],
    queryFn: () => fetchDeals({ data: { userId } }),
  });

  const data = q.data;
  const rows = data?.rows ?? [];
  const prospect = useProspectStatus(userId);
  const unclaimed =
    prospect.data?.isProspect === true && prospect.data.claimed === false;

  return (
    <div className="mx-auto max-w-7xl px-4 py-8">
      <Link to="/admin/users" className="text-sm text-brand-muted underline">
        Back to users
      </Link>
      <h1 className="mt-3 text-xl font-semibold">
        Deals for {data?.user.email ?? userId}
        {unclaimed && <ProspectBadge />}
      </h1>
      <p className="mt-1 text-sm text-brand-muted">
        Team scoped, read only.{" "}
        {data?.user.team_name ? `Team: ${data.user.team_name}.` : "No team found."}{" "}
        {rows.length} deals.
      </p>

      {unclaimed && <AdminProspectClaimPanel userId={userId} />}

      <AdminCreateDealPanel
        targetUserId={userId}
        targetEmail={data?.user.email ?? null}
        onCreated={() => void q.refetch()}
      />

      {q.isPending && <p className="mt-6 text-sm text-brand-muted">Loading.</p>}
      {q.error && (
        <p className="mt-6 text-sm text-red-600">{(q.error as Error).message}</p>
      )}

      {!q.isPending && rows.length === 0 && (
        <p className="mt-6 text-sm text-brand-muted">No deals for this team.</p>
      )}

      {rows.length > 0 && (
        <div className="mt-6 overflow-x-auto rounded-md border border-brand-line">
          <table className="w-full text-left text-sm">
            <thead className="bg-brand-paper-2 text-xs uppercase text-brand-muted">
              <tr>
                <th className="px-3 py-2">Deal</th>
                <th className="px-3 py-2">Owner</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Created</th>
                <th className="px-3 py-2">Sector</th>
                <th className="px-3 py-2 text-right">Size</th>
                <th className="px-3 py-2 text-right">Deliveries</th>
                <th className="px-3 py-2">Latest outcome</th>
                <th className="px-3 py-2 text-right">Delivered (latest)</th>
                <th className="px-3 py-2 text-right">Delivered (total)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => {
                const archived = d.status === "archived";
                return (
                  <tr
                    key={d.id}
                    className={
                      archived
                        ? "border-t border-brand-line bg-brand-paper-2/60 text-brand-muted italic"
                        : "border-t border-brand-line"
                    }
                  >
                    <td className="px-3 py-2">
                      <Link
                        to="/admin/users/$userId/deals/$dealId"
                        params={{ userId, dealId: d.id }}
                        className="underline"
                      >
                        {d.name ?? "Untitled deal"}
                      </Link>
                      {d.is_internal && (
                        <span className="ml-2 rounded-full border border-brand-line px-1.5 py-0.5 align-middle text-[10.5px] uppercase tracking-wide text-brand-muted">
                          Internal
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">{d.owner_email ?? d.owner_user_id}</td>
                    <td className="px-3 py-2">{d.status ?? ""}</td>
                    <td className="px-3 py-2 text-brand-muted">{fmtDate(d.created_at)}</td>
                    <td className="px-3 py-2">{d.sector ?? ""}</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {fmtMoney(d.deal_size_usd)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{d.runs_count}</td>
                    <td className="px-3 py-2">
                      {d.latest_status ?? ""}
                      {d.latest_error_class ? ` (${d.latest_error_class})` : ""}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {d.latest_delivered ?? ""}
                      {d.latest_target ? ` / ${d.latest_target}` : ""}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{d.total_delivered}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
